import { useState } from 'react';

import { Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import { useGetSubset } from '../../../../../api/queries/useGetSubset';
import { hasAnyOngoingRunOnAllDocsCase } from '../../../../../helpers/runs';
import { useJobProgress } from '../../../../../hooks/useJobProgress';
import useAddDocsToMatter from '@/api/mutations/useAddDocsToMatter';
import { useGetDuplicateScanStatus } from '@/api/queries/useGetDuplicateScanStatus';
import { useGetFiles } from '@/api/queries/useGetFiles';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { FileUploader } from '@/new/components/file-uploader';
import { Icon } from '@/new/components/icon';
import { DocumentUploadOnboarding } from '@/new/components/onboarding/document-upload-onboarding';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';

export function DocumentUpload() {
  const { matterId } = useParams();
  const { hasPermission } = useHasPermission();
  const canUploadDocuments = hasPermission(Permission.DocumentUpload);
  const navigate = useNavigate();
  const location = useLocation();
  const { data: matter } = useGetMatterV2(matterId ?? '');
  const allDocumentsCaseId = matter?.allDocumentsCaseId ?? undefined;
  const { activeJobs } = useJobProgress(allDocumentsCaseId ?? '');
  const { mutateAsync: addDocs, isLoading } = useAddDocsToMatter(matterId ?? '');
  const { refetch: refetchScanStatus } = useGetDuplicateScanStatus(matterId ?? '');
  const { data: subset } = useGetSubset(matterId ?? '', allDocumentsCaseId);
  const { data: files } = useGetFiles(matterId ?? '');
  const [filesProcessing, setFilesProcessing] = useState(false);
  const [filesExist, setFilesExist] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!matterId) return null;
  if (!canUploadDocuments) {
    return <Navigate to={`/matters/${matterId}`} replace />;
  }
  if (matter?.isDraft) {
    return <Navigate to={`/matters/new/${matterId}`} replace />;
  }
  if (matter && !matter.issues) {
    toast.info('Add Matter issues before processing additional documents.');
    return <Navigate to={`/matters/${matterId}/settings/issues`} replace />;
  }

  const isRunOngoing = hasAnyOngoingRunOnAllDocsCase(subset?.runs, allDocumentsCaseId ?? '');
  const fromChecklistDocumentUpload = Boolean(location.state?.fromChecklistDocumentUpload);

  const handleProcessDocuments = async () => {
    try {
      const { data: scanStatus } = await refetchScanStatus();
      if (scanStatus?.scanning) {
        toast.info('Please wait for the duplicate scan to complete before processing documents.');
        return;
      }

      await addDocs();
      toast.success('Documents are being processed');
      navigate(`/matters/${matterId}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to add documents');
    }
  };

  return (
    <>
      <Breadcrumb
        items={
          matter
            ? [
                { href: '/matters', label: 'Matters', active: false },
                { href: `/matters/${matterId}`, label: matter.name, active: false, iconClass: 'lucide--folder' },
                { href: `/matters/${matterId}/docs/upload`, label: 'Upload Documents', active: true },
              ]
            : [{ href: '/matters', label: 'Matters', active: false }]
        }
      />
      <PageContainer className="border-b">
        <PageHeader>
          <PageTitle text="Upload Documents" subtext="Add more documents to your matter." />

          <Button
            variant="primary"
            disabled={filesProcessing || !filesExist || isLoading || activeJobs.length > 0 || isRunOngoing}
            onClick={() => setConfirmOpen(true)}
          >
            {isLoading ? 'Processing...' : 'Process Documents'}
            <Icon className="lucide--arrow-right" />
          </Button>
        </PageHeader>
      </PageContainer>

      <ConfirmationModal
        isOpen={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Process Documents"
        onConfirm={handleProcessDocuments}
        isLoading={isLoading}
      >
        <p>
          Are you sure you want to process{' '}
          <strong>
            {files?.length ?? 0} {files?.length === 1 ? 'file' : 'files'}
          </strong>
          ?
        </p>
      </ConfirmationModal>

      <FileUploader setFilesProcessing={setFilesProcessing} setFilesExist={setFilesExist} />
      {fromChecklistDocumentUpload && <DocumentUploadOnboarding />}
    </>
  );
}
